import { Stars } from "assets/icons/Stars";
import { LoadingSpinner } from "components/LoadingSpinner";
import { ToastContext } from "context/ToastContext";
import {
  feeRateSourceFromEffectiveDate,
  parseDateOnlyParts,
} from "helpers/date";
import { centsToDollars, parsePrice } from "helpers/price";
import { Label } from "layout/typography/Label";
import { debounce } from "lodash";
import { HorizontalLine } from "pages/Dashboard/components/styled";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Paginator, PaginatorPageState } from "primereact/paginator";
import { Tooltip } from "primereact/tooltip";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useGetContractInsightsRecommendationsMutation } from "store/queries/contractInsights";
import { useGetAvailableCompetitors } from "store/queries/marketPrices";
import {
  useGetTotalAllowableAndPatientPortionQuery,
  useGetTopCaseRatesWithPayersQuery,
} from "store/queries/providerPayout";
import {
  IProviderProcedureComparisonRequest,
  useGetAllProviderProcedureComparisonQuery,
  useLazyGetAllProviderProcedureComparisonQuery,
  useGetTop10ProviderProcedureComparisonQuery,
  useGetPayerTermsForAccountQuery,
  useGetContractManagementAvailableFiltersQuery,
} from "store/queries/providerProcedure";
import { useGetProvidersWithProceduresByAccountQuery } from "store/queries/providers";
import styled from "styled-components";
import { IPayer, IPayerWithProcedurePrice } from "types/Payers/Payers";
import { IPayerTerm, IPayerWithTerms } from "types/payerTerms";
import { SpecialtyList, SpecialtyListDict } from "types/Provider/Provider";

import { AllowableVsPtResponsibility } from "../AllowableVsPtResponsibility";
import { ContractModelingAnalysis } from "../ContractModelingAnalysis/ContractModelingAnalysis";
import { MarketPriceAnalysis } from "../MarketPriceAnalysis";
import { TopCasesWithPayers } from "../TopCasesWithPayers";
import { CptCodeMultiSelect } from "./components/CptCodeMultiSelect";
import { Insights } from "./components/Insights";
import { exportToExcel, formatCodeList, matchModeOptions } from "./helpers";

const StyledTopCPTCodesTag = styled.div<{ active?: boolean }>`
  border: 1px solid
    ${(props) => (props.active ? `var(--primary-color)` : "#D0D5DD")};
  padding: 9px 14px;
  border-radius: 22px;
  color: ${(props) =>
    props.active ? `var(--primary-color)` : "var(--gray-new-500)"};
  font-weight: 600;
  cursor: pointer;
  font-size: 14px;
`;

const StyledContainer = styled.div`
  border-radius: 8px;
  border: 1px solid var(--gray-new-200);
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledInputText = styled(InputText)`
  border: none;
  :enabled:focus {
    border: none !important;
    box-shadow: none !important;
  }
`;

const StyledInputTextContainer = styled.div`
  border: 1px solid #ced4da;
  border-radius: 6px;
  padding-left: 0.75rem;
  display: flex;
  align-items: center;
`;

const StyledDataTable = styled(DataTable)<{ volumeFilterActive?: boolean }>`
  ${(props) =>
    props.volumeFilterActive &&
    `
    .p-datatable-thead th:nth-child(3) .p-column-filter-menu-button {
      background-color: var(--primary-300) !important;
      color: white !important;
    }
  `}
`;

// Use local-noon date construction (via parseDateOnlyParts) so calendar-only
// `YYYY-MM-DD` strings from the backend don't get shifted a day earlier in
// negative-UTC timezones. Mirrors `formatDateOnlyForDisplay` used on the
// Contract View page so dropdown labels match the contract effective date.
function formatEffectiveDate(iso: string): string {
  const parts = parseDateOnlyParts(iso);
  const date = parts
    ? new Date(parts.y, parts.m, parts.d, 12, 0, 0, 0)
    : new Date(iso);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatTermLabel(term: IPayerTerm): string {
  const end = term.endDate ? formatEffectiveDate(term.endDate) : "Open";
  return `${formatEffectiveDate(term.effectiveDate)} → ${end}`;
}

// For payers with 0 or 1 term we hide the group header (it would just repeat
// the payer name shown by the single item below it). The marker element is
// the hook for `.payer-term-select-panel` CSS in global.css.
function payerGroupTemplate(group: { label: string; collapsed?: boolean }) {
  return group.collapsed ? (
    <span data-collapsed-group="" />
  ) : (
    <span>{group.label}</span>
  );
}

// Keys encode (payerId, termId) as "payerId:termId". Term ids from the backend
// contain colons (ISO timestamps in the synthetic id), so we must only split on
// the FIRST colon — anything after is the full term id.
function parsePayerTermKey(key: string): {
  payerId: number;
  termId: string;
} {
  const idx = key.indexOf(":");
  if (idx < 0) return { payerId: Number(key), termId: "" };
  return { payerId: Number(key.slice(0, idx)), termId: key.slice(idx + 1) };
}

export function FeeScheduleComparison({ accountId }: { accountId: string }) {
  const { current: toastElement } = useContext(ToastContext);
  const [inputValues, setInputValues] = useState<{ [key: string]: any }>({}); // Store input values
  const [topResultsAmount, setTopResultsAmount] = useState(10);
  const [isDownloading, setIsDownloading] = useState(false);
  const [volumeFilter, setVolumeFilter] = useState<number | null>(null);
  const [matchMode, setMatchMode] = useState<string>("equals");
  const [selectedPayerTermKeys, setSelectedPayerTermKeys] = useState<string[]>(
    []
  );
  const [filters, setFilters] = useState<IProviderProcedureComparisonRequest>({
    payerNameField: undefined,
    cptCodes: undefined,
    serviceLines: undefined,
    cptDescription: undefined,
    providerId: undefined,
    providerName: undefined,
    onlyLowerThanMedicare: false,
    volume: undefined,
    volumeMatchMode: "equals",
  });
  const [first, setFirst] = useState(0);
  const [rows] = useState(10);
  const [
    firstAllowableVsPtResponsibility,
    setFirstAllowableVsPtResponsibility,
  ] = useState(0);
  const [rowsAllowableVsPtResponsibility] = useState(25);
  const [rowsTopCasesWithPayers] = useState(25);
  const hasSetDefaultPayers = useRef(false);
  const [triggerDownloadQuery] =
    useLazyGetAllProviderProcedureComparisonQuery();

  const { data: payerTermsData } = useGetPayerTermsForAccountQuery(
    { accountId, providerId: filters.providerId },
    { skip: !filters.providerId }
  );
  // Providers that have at least one procedure live for this account.
  const {
    data: providersWithProcedures,
    isLoading: isProvidersWithProceduresLoading,
    isFetching: isProvidersWithProceduresFetching,
  } = useGetProvidersWithProceduresByAccountQuery(
    { accountId },
    { skip: !accountId }
  );

  // Default the provider filter to the account's facility, falling back to the first available
  // provider. Facility rates are what a contract-rate comparison is usually about, so landing there
  // beats landing on whichever provider happens to sort first alphabetically.
  useEffect(() => {
    const providers = providersWithProcedures?.data;
    if (providers?.length && !filters.providerId) {
      const defaultProvider =
        providers.find((p) => p.specialty === SpecialtyList.FACILITY) ??
        providers[0];
      setFilters((prev) => ({ ...prev, providerId: defaultProvider.id }));
    }
  }, [providersWithProcedures, filters.providerId]);

  // Both dropdowns read from the same source as the table — scoped to
  // (account, provider) on ProviderProcedure. Avoids the prior issue where a
  // CPT picked from the EDI-claim catalog produced empty fee-schedule rows.
  const {
    data: availableFilters = { cptCodes: [], serviceLines: [] },
    isLoading: isLoadingAvailableFilters,
  } = useGetContractManagementAvailableFiltersQuery(
    { accountId, providerId: filters.providerId },
    { skip: !accountId || !filters.providerId }
  );
  const cptCodeOptions = availableFilters.cptCodes;
  const serviceLineOptions = availableFilters.serviceLines;
  const isLoadingCptOptions = isLoadingAvailableFilters;
  const hasServiceLines = serviceLineOptions.length > 0;
  // Memoize the option list so MultiSelect doesn't re-diff a new array on
  // every parent render.
  const serviceLineSelectOptions = useMemo(
    () => serviceLineOptions.map((sl) => ({ label: sl, value: sl })),
    [serviceLineOptions]
  );
  const selectCptCodes = (codes: string[]) => {
    setFilters((prev) => ({
      ...prev,
      cptCodes: codes.length > 0 ? codes : undefined,
    }));
    setFirst(0);
  };

  // Codes pasted into the CPT search that have no row for this account +
  // provider can't be selected (the dropdown options and the table read from
  // the same source), so report them instead of dropping them silently.
  const reportUnmatchedCptCodes = ({
    unmatched,
    matchedCount,
    totalCount,
  }: {
    unmatched: string[];
    matchedCount: number;
    totalCount: number;
  }) => {
    toastElement?.show({
      severity: matchedCount > 0 ? "warn" : "error",
      summary:
        matchedCount > 0
          ? `Added ${matchedCount} of ${totalCount} codes`
          : "No matching CPT codes",
      detail: `No rates for this CPT codes: ${formatCodeList(unmatched)}`,
      life: 6000,
    });
  };

  // Check if market price data exists for the account and selected provider
  const {
    data: availableCompetitors,
    isLoading: isCompetitorsLoading,
    isFetching: isCompetitorsFetching,
  } = useGetAvailableCompetitors(
    { providerId: filters.providerId },
    { skip: !filters.providerId }
  );

  // Check if market price data exists for the account and selected provider
  const hasMarketPriceData = useMemo(() => {
    return availableCompetitors && availableCompetitors.length > 0;
  }, [availableCompetitors]);

  const {
    isLoading,
    isFetching,
    data: feeSchedulesFromAPI,
  } = useGetAllProviderProcedureComparisonQuery(
    {
      accountId,
      page: first,
      pageSize: rows,
      ...filters,
    },
    {
      // Wait for a provider and for payer terms to load before firing, so the
      // first fetch already carries the default `contractFeeScheduleIds` and we
      // don't fetch once unscoped then again once defaults arrive. We do NOT gate
      // on a payer being selected: `contractFeeScheduleIds` only narrows the
      // payer price columns — the CPT/procedure/service-line/volume rows come
      // back regardless — so clearing all payers (or an account whose provider
      // has none of the default payers) must still load the base table.
      skip: !filters.providerId || payerTermsData === undefined,
    }
  );
  const {
    data: top10ProviderProcedureComparison,
    isLoading: isTop10DataLoading,
    isFetching: isTop10DataFetching,
  } = useGetTop10ProviderProcedureComparisonQuery(
    {
      accountId,
      providerId: filters.providerId,
      limit: topResultsAmount,
    },
    {
      skip: !accountId || !filters.providerId,
    }
  );

  const {
    isLoading: isAllowableVsPtResponsibilityLoading,
    isFetching: isAllowableVsPtResponsibilityFetching,
    data: dataFromAPIAllowableVsPtResponsibility,
  } = useGetTotalAllowableAndPatientPortionQuery({
    page: firstAllowableVsPtResponsibility,
    pageSize: rowsAllowableVsPtResponsibility,
    accountId,
  });

  const [
    getContractInsightsRecommendations,
    { data: contractInsightsData, isLoading: isLoadingContractInsights },
  ] = useGetContractInsightsRecommendationsMutation();

  const {
    isLoading: isTopCasesWithPayersLoading,
    isFetching: isTopCasesWithPayersFetching,
    data: dataFromAPITopCasesWithPayers,
  } = useGetTopCaseRatesWithPayersQuery({
    pageSize: rowsTopCasesWithPayers,
    accountId,
  });

  const debouncedSetFilters = debounce(
    (
      updater: (
        prev: IProviderProcedureComparisonRequest
      ) => IProviderProcedureComparisonRequest
    ) => {
      setFilters((prev) => updater(prev));
    },
    500
  );

  useEffect(() => {
    if (!Object.keys(inputValues).length) {
      return undefined;
    }
    debouncedSetFilters((prevFilters: IProviderProcedureComparisonRequest) => ({
      ...prevFilters,
      ...inputValues,
    }));
    return () => {
      debouncedSetFilters.cancel();
    };
  }, [inputValues]);

  // Reset default payers flag when the selected provider changes
  useEffect(() => {
    hasSetDefaultPayers.current = false;
  }, [filters.providerId]);

  const payerTermsById = useMemo(() => {
    const map = new Map<number, IPayerWithTerms>();
    payerTermsData?.forEach((pt) => map.set(pt.payerId, pt));
    return map;
  }, [payerTermsData]);

  // Grouped options: each payer is a group; items are its terms. Sourced from
  // payerTermsData (stable across filter changes) — sourcing from the fee
  // schedule response would cause the options list to shrink as the user
  // narrows the filter, making other payers disappear.
  const groupedPayerOptions = useMemo(() => {
    return (payerTermsData ?? [])
      .slice()
      .sort((a, b) => a.payerName.localeCompare(b.payerName))
      .map((pt) => {
        // Only break a payer into per-term items when there are multiple terms
        // to disambiguate. Zero or one term collapses to a single payer-only
        // option so the user picks the payer directly. When collapsed, the
        // group header is hidden via CSS (see `.payer-term-select-panel` rule
        // in global.css) so the payer name only appears once — as the item.
        const collapsed = pt.terms.length <= 1;
        const items = collapsed
          ? [
              {
                label: pt.payerName,
                range: pt.payerName,
                value: `${pt.payerId}:`,
                isPayerHeader: true,
              },
            ]
          : pt.terms.map((term) => ({
              // `label` drives the chip text when selected ("Cigna Apr 20, 2026");
              // `range` is what `itemTemplate` renders inside the open dropdown
              // (just the date range, since the payer name is already in the
              // group header).
              label: `${pt.payerName} ${formatEffectiveDate(
                term.effectiveDate
              )}`,
              range: formatTermLabel(term),
              value: `${pt.payerId}:${term.id}`,
              isPayerHeader: false,
            }));
        return {
          label: pt.payerName,
          payerId: pt.payerId,
          collapsed,
          items,
        };
      });
  }, [payerTermsData]);

  // One column per selected (payer, term) key — so the same payer can appear
  // multiple times side-by-side, one column per term the user picked.
  const selectedPayerColumns = useMemo<
    {
      key: string;
      payerId: number;
      payerName: string;
      termId: string;
      effectiveDate: string | null;
      contractFeeScheduleIds: string[];
    }[]
  >(() => {
    const apiPayerMap = new Map<number, IPayer>();
    (feeSchedulesFromAPI?.payers ?? []).forEach((p) =>
      apiPayerMap.set(p.id, p)
    );
    return selectedPayerTermKeys.flatMap((key) => {
      const { payerId, termId } = parsePayerTermKey(key);
      const payerName =
        apiPayerMap.get(payerId)?.name ??
        payerTermsById.get(payerId)?.payerName;
      if (!payerName) return [];
      const term = termId
        ? payerTermsById.get(payerId)?.terms.find((t) => t.id === termId)
        : undefined;
      return [
        {
          key,
          payerId,
          payerName,
          termId,
          effectiveDate: term?.effectiveDate ?? null,
          contractFeeScheduleIds: term?.contractFeeScheduleIds ?? [],
        },
      ];
    });
  }, [selectedPayerTermKeys, feeSchedulesFromAPI?.payers, payerTermsById]);

  // Distinct payers among the selected columns — used for behaviors that scope
  // per-payer (e.g. Volume source) and for the CSV download filter.
  const selectedPayers = useMemo<{ id: number; name: string }[]>(() => {
    const seen = new Map<number, string>();
    selectedPayerColumns.forEach((c) => {
      if (!seen.has(c.payerId)) seen.set(c.payerId, c.payerName);
    });
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [selectedPayerColumns]);

  // Set default selection once payer-terms data is available.
  useEffect(() => {
    if (!hasSetDefaultPayers.current && payerTermsData !== undefined) {
      const defaultPayerNames = [
        "Cigna PPO",
        "United Healthcare",
        "Medicare",
        "Aetna",
        "BC Select / Plus /Traditional",
      ];
      const keys = (payerTermsData ?? [])
        .filter((pt) => defaultPayerNames.includes(pt.payerName))
        .map((pt) => {
          // Match the dropdown: a payer with 0 or 1 term has no per-term
          // option, so default-select the payer directly (empty termId).
          if (pt.terms.length <= 1) return `${pt.payerId}:`;
          const currentTerm = pt.terms.find((t) => t.isCurrent);
          return `${pt.payerId}:${currentTerm?.id ?? ""}`;
        });
      setSelectedPayerTermKeys(keys);
      hasSetDefaultPayers.current = true;
    }
  }, [payerTermsData]);

  // Any combination of (payer, term) keys is allowed — multiple terms of the
  // same payer render as separate columns.
  const handlePayerTermChange = (nextValues: string[]) => {
    setSelectedPayerTermKeys(nextValues);
  };

  // Open-dropdown item renderer: under a payer-name group header we only need
  // the date range (payer name would be redundant). Chip text for selected
  // values uses each option's `label` ("Cigna Apr 20, 2026") so default chip
  // styling (blue pill + × button) is preserved. For collapsed (0/1-term)
  // payers we render the payer name in semibold so it visually reads as a
  // payer entry rather than a term row.
  const payerItemTemplate = (option: {
    range: string;
    isPayerHeader?: boolean;
  }) => (
    <span className={option.isPayerHeader ? "font-semibold" : undefined}>
      {option.range}
    </span>
  );

  const contractFeeScheduleIds = useMemo(() => {
    return selectedPayerTermKeys.flatMap((key) => {
      const { payerId, termId } = parsePayerTermKey(key);
      const payerTerms = payerTermsById.get(payerId)?.terms ?? [];
      // Collapsed selection (empty termId) means the dropdown rendered this
      // payer as a single item because it has 0 or 1 terms. For the 1-term
      // case we still need to send the term's contract fee schedules —
      // otherwise the request silently unscopes the payer and the backend
      // returns rows from every term (including expired ones), not the
      // single term the user thinks they picked.
      if (!termId) {
        return payerTerms.length === 1
          ? payerTerms[0].contractFeeScheduleIds ?? []
          : [];
      }
      return (
        payerTerms.find((t) => t.id === termId)?.contractFeeScheduleIds ?? []
      );
    });
  }, [selectedPayerTermKeys, payerTermsById]);

  useEffect(() => {
    setFilters((prev) => {
      const next =
        contractFeeScheduleIds.length > 0 ? contractFeeScheduleIds : undefined;
      if (
        JSON.stringify(prev.contractFeeScheduleIds) === JSON.stringify(next)
      ) {
        return prev;
      }
      return { ...prev, contractFeeScheduleIds: next };
    });
  }, [contractFeeScheduleIds]);

  // When exactly one payer is selected, scope the backend query to that payer
  // so the Volume column uses any payer-specific custom CPT volume upload for
  // that payer. When 0 or 2+ payers are selected, clear the scope so volume
  // falls back to the account-wide upload or the default (EDI + local orders).
  useEffect(() => {
    const nextPayerName =
      selectedPayers?.length === 1 ? selectedPayers[0].name : undefined;
    setFilters((prev) =>
      prev.payerNameField === nextPayerName
        ? prev
        : { ...prev, payerNameField: nextPayerName }
    );
  }, [selectedPayers]);

  useEffect(() => {
    if (accountId && filters.providerId) {
      getContractInsightsRecommendations({
        accountId,
        providerId: filters.providerId,
      });
    }
  }, [accountId, filters.providerId, getContractInsightsRecommendations]);

  const feeSchedules = useMemo(() => {
    return feeSchedulesFromAPI?.data.map((feeSchedule) => {
      return {
        procedureId: feeSchedule.procedureId,
        cptCode: feeSchedule?.procedure?.cptCode,
        cptDescription: feeSchedule?.procedure?.description,
        serviceLine: feeSchedule?.procedure?.serviceLine ?? null,
        provider: feeSchedule?.provider?.name,
        specialty: SpecialtyListDict[feeSchedule?.provider?.specialty],
        connectedHospital: feeSchedule?.provider?.account.name,
        payers: feeSchedule?.payers,
        volume: feeSchedule?.procedure.orderCount,
      };
    });
  }, [feeSchedulesFromAPI]);

  const handleDownloadData = async () => {
    try {
      setIsDownloading(true);
      const result = await triggerDownloadQuery({
        accountId,
        ...filters,
      }).unwrap();
      const fullData = result.data;
      const flattenedData = fullData.map((item) => {
        // One CSV column per selected (payer, term) pair. Column key:
        // "PayerName (Apr 20, 2026)" — or just "PayerName" when no term info.
        // Fall back to payer-id match when the backend hasn't attached
        // `contractFeeScheduleId` per entry yet.
        const payerMap =
          selectedPayerColumns.length > 0
            ? Object.fromEntries(
                selectedPayerColumns.map((col) => {
                  const entries = (item.payers ?? []).filter(
                    (p) => p.id === col.payerId
                  );
                  const entry =
                    entries.find(
                      (p) =>
                        p.contractFeeScheduleId &&
                        col.contractFeeScheduleIds.includes(
                          p.contractFeeScheduleId
                        )
                    ) ?? entries[0];
                  const header = col.effectiveDate
                    ? `${col.payerName} (${formatEffectiveDate(
                        col.effectiveDate
                      )})`
                    : col.payerName;
                  return [
                    header,
                    entry?.priceInCents
                      ? centsToDollars(entry.priceInCents)
                      : null,
                  ];
                })
              )
            : Object.fromEntries(
                (item.payers ?? []).map((payer) => [
                  payer.name,
                  payer.priceInCents
                    ? centsToDollars(payer.priceInCents)
                    : null,
                ])
              );

        return {
          CPT_Code: item.procedure?.cptCode,
          Procedure: item.procedure?.description,
          Service_Line: item.procedure?.serviceLine ?? "",
          Provider: item.provider?.name,
          Specialty: item.provider?.specialty,
          Hospital: item.provider?.account?.name,
          Volume: item.procedure?.orderCount,
          ...payerMap,
        };
      });

      await exportToExcel(flattenedData, "fee_schedule.csv");
    } catch (error) {
      console.error("Error downloading data:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const onPageChange = (event: PaginatorPageState) => {
    setFirst(event.first);
  };

  function payerColumn(
    row: any,
    col: {
      payerId: number;
      contractFeeScheduleIds: string[];
    }
  ) {
    const { payerId, contractFeeScheduleIds: colCfsIds } = col;
    // Prefer an entry tied to one of this column's CFS ids (multi-term: several
    // entries per payer, one per term). Fall back to match-by-payer for rows
    // returned before the backend adds `contractFeeScheduleId` per entry.
    const payerEntries = row.payers.filter(
      (p: IPayerWithProcedurePrice) => p.id === payerId
    );
    const payerPrice =
      payerEntries.find(
        (p: IPayerWithProcedurePrice) =>
          p.contractFeeScheduleId && colCfsIds.includes(p.contractFeeScheduleId)
      ) ?? payerEntries[0];
    const isLowerThanMedicare = !!payerPrice?.lowerThanMedicare;
    const hasImplantCost =
      payerPrice?.implantCostInCents && payerPrice.implantCostInCents > 0;
    const totalWithImplant = hasImplantCost
      ? (payerPrice.priceInCents || 0) + payerPrice.implantCostInCents
      : null;

    const rateSource = feeRateSourceFromEffectiveDate(
      payerPrice?.contractFeeScheduleEffectiveDate
    );
    const mainPrice =
      payerPrice?.priceInCents != null
        ? parsePrice(centsToDollars(payerPrice.priceInCents))
        : "-";

    const renderPriceContent = () => (
      <div className="flex flex-column">
        <div
          className="flex flex-column gap-1"
          aria-label={
            mainPrice !== "-"
              ? `${mainPrice}, ${rateSource.ariaPriceModifier}`
              : "No rate"
          }
        >
          <div className="flex gap-1 align-items-center">
            {mainPrice}
            {isLowerThanMedicare && (
              <i className="pi pi-caret-down text-red-500" />
            )}
          </div>
          {mainPrice !== "-" && (
            <span className="text-xs text-gray-500" aria-hidden="true">
              {rateSource.caption}
            </span>
          )}
        </div>
        {hasImplantCost && (
          <>
            <Tooltip
              target={`.implant-tooltip-${row.procedureId}-${payerId}`}
            />
            <div
              className={`implant-tooltip-${row.procedureId}-${payerId} cursor-pointer`}
              data-pr-tooltip={`Implant Payment is ${parsePrice(
                centsToDollars(payerPrice.implantCostInCents)
              )}`}
              style={{
                fontSize: "0.75rem",
                fontStyle: "italic",
                color: "#6b7280",
                marginTop: "2px",
              }}
            >
              Total: {parsePrice(centsToDollars(totalWithImplant))}
            </div>
          </>
        )}
      </div>
    );

    if (!isLowerThanMedicare) {
      return renderPriceContent();
    }

    return (
      <>
        <Tooltip target={`.tooltip-${row.procedureId}-${payerId}`} />
        <div
          className={`cursor-pointer tooltip-${row.procedureId}-${payerId}`}
          data-pr-tooltip="Lower than Medicare"
        >
          {renderPriceContent()}
        </div>
      </>
    );
  }

  const genericInputFilterTemplate = (
    fieldName: string,
    filterName: string,
    placeholder: string
  ) => {
    const isCpt = filterName === "cptCodeField";
    return (
      <StyledInputTextContainer className={isCpt ? "lg:w-16rem" : "lg:w-20rem"}>
        <i className="pi pi-search" style={{ color: "#98A2B3" }} />
        <StyledInputText
          className="p-column-filter no-icon max-h-3rem"
          id={`${fieldName}-filter`}
          autoComplete="off"
          placeholder={placeholder}
          aria-labelledby="filter"
          value={inputValues[filterName] || ""}
          onChange={(e) => {
            setInputValues((prevState) => ({
              ...prevState,
              [filterName]: e.target.value,
            }));
          }}
        />
      </StyledInputTextContainer>
    );
  };

  const handleVolumeFilter = () => {
    const value = volumeFilter;
    const mode = matchMode;

    setFilters((prev) => ({
      ...prev,
      volume: value === null || value === undefined ? undefined : value,
      volumeMatchMode: mode || "equals",
    }));
  };

  const volumeFilterTemplate = () => {
    return (
      <div className="flex flex-column gap-2">
        <Dropdown
          value={matchMode}
          onChange={(e) => setMatchMode(e.value)}
          options={matchModeOptions}
          optionLabel="label"
          placeholder="Select a Match Mode"
          className="w-full"
        />
        <InputNumber
          value={volumeFilter}
          onChange={(e) => setVolumeFilter(e.value)}
          placeholder="Volume"
        />
        <div className="flex justify-content-between gap-2 mt-3">
          <Button
            label="Clear"
            icon="pi pi-times"
            className="p-button-outlined"
            onClick={() => {
              setVolumeFilter(null);
              setMatchMode("equals");
              setFilters((prev) => ({
                ...prev,
                volume: undefined,
                volumeMatchMode: "equals",
              }));
            }}
          />
          <Button
            label="Apply"
            icon="pi pi-check"
            onClick={() => handleVolumeFilter()}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-column gap-4" style={{ position: "relative" }}>
      {isLoading ? (
        <div className="flex flex-column gap-3 align-items-center">
          <LoadingSpinner />
          <p className="text-gray-600 text-center">Generating AI Insights...</p>
        </div>
      ) : (
        <div className="flex flex-column gap-3">
          <div className="flex gap-2 align-items-center md:flex-row flex-column">
            <div className="flex gap-2 align-items-center">
              <Stars
                className="text-2xl text-primary"
                style={{ minWidth: "24px" }}
              />
              <p className="text-primary text-lg md:text-2xl font-semibold">
                Payer AI Insights
              </p>
            </div>
            <Dropdown
              value={filters.providerId}
              className="p-column-filter w-full md:w-16rem ml-4"
              placeholder="Provider"
              options={(providersWithProcedures?.data ?? []).map(
                (provider) => ({
                  value: provider.id,
                  label: provider.name,
                })
              )}
              onChange={(e) => {
                setFilters((prevState) => ({
                  ...prevState,
                  providerId: e.target.value,
                }));
              }}
            />
          </div>
          <div className="flex flex-column gap-2">
            <Label htmlFor="top-payers" className="text-sm text-gray-new-700">
              Results for
            </Label>
            <div className="flex gap-3">
              <StyledTopCPTCodesTag
                active={topResultsAmount === 10}
                className="bg-white"
                onClick={() => setTopResultsAmount(10)}
              >
                Top 10 CPT Codes
              </StyledTopCPTCodesTag>
              <StyledTopCPTCodesTag
                active={topResultsAmount === 100}
                className="bg-white"
                onClick={() => setTopResultsAmount(100)}
              >
                Top 100 CPT Codes
              </StyledTopCPTCodesTag>
            </div>
          </div>
          {isTop10DataLoading ||
          isTop10DataFetching ||
          isAllowableVsPtResponsibilityLoading ||
          isAllowableVsPtResponsibilityFetching ||
          isProvidersWithProceduresLoading ||
          isProvidersWithProceduresFetching ||
          isCompetitorsLoading ||
          isCompetitorsFetching ? (
            <LoadingSpinner />
          ) : (
            <Insights
              top10Data={top10ProviderProcedureComparison?.data}
              topResultsAmount={topResultsAmount}
              dataFromAPIAllowableVsPtResponsibility={
                dataFromAPIAllowableVsPtResponsibility
              }
              hasMarketPriceData={hasMarketPriceData}
              contractInsightsData={contractInsightsData}
              isLoadingContractInsights={isLoadingContractInsights}
            />
          )}
          <StyledContainer className="bg-white">
            <div className="p-4 flex flex-column gap-2">
              <p className="text-md font-bold mb-2">All Payer Rates</p>
              <div className="flex lg:flex-row flex-column justify-content-between lg:align-items-center gap-3">
                <div className="flex lg:flex-row flex-column align-items-start gap-4">
                  <div className="flex flex-column gap-2">
                    <Label htmlFor="payers" className="text-sm text-gray-700">
                      Selected Payers To Show
                    </Label>
                    <MultiSelect
                      value={selectedPayerTermKeys}
                      onChange={(e) => handlePayerTermChange(e.value ?? [])}
                      options={groupedPayerOptions}
                      optionLabel="label"
                      optionValue="value"
                      optionGroupLabel="label"
                      optionGroupChildren="items"
                      placeholder="Show Payers"
                      className="w-full md:w-36rem"
                      panelClassName="payer-term-select-panel"
                      showClear
                      filter
                      display="chip"
                      itemTemplate={payerItemTemplate}
                      optionGroupTemplate={payerGroupTemplate}
                      maxSelectedLabels={3}
                      selectedItemsLabel="{0} payers selected"
                      style={{
                        background:
                          selectedPayerTermKeys.length > 0
                            ? "#F9FAFB"
                            : "white",
                        border: "1px solid #D0D5DD",
                        boxShadow:
                          selectedPayerTermKeys.length > 0
                            ? "0px 1px 2px rgba(16, 24, 40, 0.05)"
                            : "none",
                        color: "#475467",
                      }}
                    />
                  </div>
                  <div className="flex gap-3 flex-column">
                    <Label htmlFor="payers" className="text-sm text-gray-700">
                      Show Prices Lower than Medicare
                    </Label>
                    <InputSwitch
                      checked={filters.onlyLowerThanMedicare}
                      onChange={(e) =>
                        setInputValues((prevState) => ({
                          ...prevState,
                          onlyLowerThanMedicare: e.value,
                        }))
                      }
                    />
                  </div>
                </div>
                <Button
                  label="Download"
                  icon="pi pi-download"
                  onClick={handleDownloadData}
                  loading={isDownloading}
                  className="p-button-outlined max-h-3rem max-w-12rem"
                />
              </div>
              <HorizontalLine className="border-gray-new-200 " />
              <div className="flex gap-3 xl:flex-row flex-column align-items-start xl:align-items-end">
                <div className="flex gap-3 flex-column lg:flex-row ">
                  <div className="flex flex-column gap-2">
                    <CptCodeMultiSelect
                      options={cptCodeOptions}
                      value={filters.cptCodes ?? []}
                      onChange={selectCptCodes}
                      onUnmatchedCodes={reportUnmatchedCptCodes}
                      isLoading={isLoadingCptOptions}
                      className="lg:w-16rem"
                    />
                  </div>
                  {genericInputFilterTemplate(
                    "Procedure",
                    "cptDescription",
                    "Search Procedure"
                  )}
                  {hasServiceLines && (
                    <div className="flex flex-column gap-2">
                      <MultiSelect
                        value={filters.serviceLines ?? []}
                        options={serviceLineSelectOptions}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            serviceLines:
                              e.value && e.value.length > 0
                                ? e.value
                                : undefined,
                          }))
                        }
                        placeholder="Service Lines"
                        filter
                        showClear
                        display="comma"
                        maxSelectedLabels={2}
                        className="lg:w-16rem"
                        emptyFilterMessage="No service lines match"
                        virtualScrollerOptions={{ itemSize: 38 }}
                      />
                    </div>
                  )}
                </div>
              </div>

              <StyledDataTable
                rowGroupMode="rowspan"
                groupRowsBy="procedureId"
                rows={rows}
                rowHover
                stripedRows
                dataKey="id"
                editMode="cell"
                className="p-fluid datatable__FeeScheduleComparison mt-4"
                value={feeSchedules}
                loading={isFetching}
                responsiveLayout="scroll"
                emptyMessage="No fee schedule to show."
                volumeFilterActive={
                  filters.volume !== undefined && filters.volume !== null
                }
              >
                <Column
                  field="cptCode"
                  header="CPT Code"
                  style={{ minWidth: "150px" }}
                />
                <Column
                  field="cptDescription"
                  header="Procedure"
                  style={{ minWidth: "260px" }}
                />
                <Column
                  field="serviceLine"
                  header="Service Line"
                  style={{ minWidth: "160px" }}
                  body={(row) => row.serviceLine || "—"}
                />
                <Column
                  field="volume"
                  header="Volume"
                  style={{ minWidth: "100px" }}
                  filter
                  filterElement={volumeFilterTemplate}
                  showFilterOperator={false}
                  showAddButton={false}
                  showApplyButton={false}
                  showClearButton={false}
                  showFilterMatchModes={false}
                  onFilterApplyClick={handleVolumeFilter}
                  filterApply={false}
                  filterFunction={() => true}
                  dataType="numeric"
                />
                {selectedPayerColumns.map((col) => (
                  <Column
                    key={col.key}
                    field={`${col.key}_price`}
                    header={
                      <div className="flex flex-column">
                        <span>{col.payerName}</span>
                        {col.effectiveDate && (
                          <span className="text-xs text-gray-500 font-normal">
                            {formatEffectiveDate(col.effectiveDate)}
                          </span>
                        )}
                      </div>
                    }
                    style={{ minWidth: "120px" }}
                    body={(rowData) => payerColumn(rowData, col)}
                  />
                ))}
                {/* <Column
                  field="provider"
                  style={{ minWidth: "200px" }}
                  header="Provider/Hospital Name"
                /> */}
              </StyledDataTable>
              <Paginator
                first={first}
                rows={rows}
                totalRecords={feeSchedulesFromAPI?.total}
                onPageChange={onPageChange}
              />
            </div>
          </StyledContainer>
        </div>
      )}
      {filters.providerId && hasMarketPriceData && (
        <MarketPriceAnalysis
          accountId={accountId}
          providerId={filters.providerId}
        />
      )}
      <ContractModelingAnalysis providerId={filters.providerId} />
      <AllowableVsPtResponsibility
        first={firstAllowableVsPtResponsibility}
        setFirst={setFirstAllowableVsPtResponsibility}
        dataFromAPI={dataFromAPIAllowableVsPtResponsibility}
        isLoading={isAllowableVsPtResponsibilityLoading}
        isFetching={isAllowableVsPtResponsibilityFetching}
      />
      <TopCasesWithPayers
        dataFromAPI={dataFromAPITopCasesWithPayers}
        isLoading={isTopCasesWithPayersLoading}
        isFetching={isTopCasesWithPayersFetching}
        payers={feeSchedulesFromAPI?.payers || []}
      />
    </div>
  );
}
